import { useState } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Basic validation
    if (!formData.company || !formData.name || !formData.email || !formData.phone || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('company', formData.company);
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d645gtqbdoed7v0v9100', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      title: 'Head Office',
      content:
        '705, Sungkang Haeoreum Knowledge Industry Center, 32 Eungye-namgil, Siheung-si, Gyeonggi-do, Korea',
      color: 'from-teal-500 to-teal-600',
    },
    {
      icon: 'ri-phone-line',
      title: 'Office Phone',
      content: '+82-31-313-2663',
      color: 'from-teal-500 to-teal-600',
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Mobile',
      content: '+82-10-3319-6611',
      color: 'from-cyan-500 to-cyan-600',
    },
    {
      icon: 'ri-mail-line',
      title: 'Email',
      content: 'tel9060@gmail.com',
      color: 'from-teal-600 to-cyan-600',
    },
    {
      icon: 'ri-printer-line',
      title: 'Fax',
      content: '+82-504-229-7701',
      color: 'from-teal-600 to-teal-700',
    },
    {
      icon: 'ri-time-line',
      title: 'Business Hours',
      content: 'Mon-Fri 09:00 - 18:00 (KST)',
      color: 'from-cyan-500 to-teal-600',
    },
  ];

  return (
    <section id="contact" className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm md:text-base font-bold mb-4">
            CONTACT US
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-3 md:mb-4">Contact Us</h2>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto font-medium">
            If you have any inquiries about smart water meters, please feel free to contact us anytime
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-5 md:mb-8">
              Contact Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 md:gap-6 mb-6 md:mb-10">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 md:gap-4 bg-white rounded-xl p-4 md:p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100"
                >
                  <div
                    className={`w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br ${info.color} rounded-lg flex items-center justify-center flex-shrink-0`}
                  >
                    <i className={`${info.icon} text-lg md:text-2xl text-white`}></i>
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm md:text-base font-bold text-gray-500 mb-0.5 md:mb-1">
                      {info.title}
                    </h4>
                    <p className="text-base md:text-lg font-bold text-gray-900 break-words">{info.content}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="bg-white rounded-xl md:rounded-2xl shadow-lg overflow-hidden border border-gray-200">
              <div className="aspect-video">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3174.8!2d126.8028!3d37.3796!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDIyJzQ2LjYiTiAxMjbCsDQ4JzEwLjEiRQ!5e0!3m2!1sko!2skr!4v1700000000000!5m2!1sko!2skr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="ROBO Asset Grid Head Office Location - 705, Sungkang Haeoreum, 32 Eungye-namgil, Siheung-si, Gyeonggi-do"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-5 md:mb-8">Inquiry Form</h3>
            <form
              id="contact-form-en"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-xl md:rounded-2xl shadow-lg p-5 md:p-8 border border-gray-200"
            >
              <div className="space-y-4 md:space-y-6">
                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm md:text-base font-bold text-gray-700 mb-1.5 md:mb-2"
                  >
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 text-base font-medium"
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm md:text-base font-bold text-gray-700 mb-1.5 md:mb-2"
                  >
                    Contact Person <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 text-base font-medium"
                    placeholder="Enter contact person name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm md:text-base font-bold text-gray-700 mb-1.5 md:mb-2"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 text-base font-medium"
                    placeholder="example@company.com"
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm md:text-base font-bold text-gray-700 mb-1.5 md:mb-2"
                  >
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 text-base font-medium"
                    placeholder="+1-234-567-8900"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm md:text-base font-bold text-gray-700 mb-1.5 md:mb-2"
                  >
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 text-base font-medium resize-none"
                    placeholder="Enter your inquiry (max 500 characters)"
                  ></textarea>
                  <div className="text-right text-xs md:text-sm text-gray-500 mt-1 font-medium">
                    {formData.message.length} / 500
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-bold py-3 md:py-4 px-6 rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-base md:text-lg"
                >
                  {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-3 md:px-4 py-2.5 md:py-3 rounded-lg text-sm md:text-base font-medium">
                    Your inquiry has been successfully submitted. We will respond as soon as possible.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 md:px-4 py-2.5 md:py-3 rounded-lg text-sm md:text-base font-medium">
                    Failed to submit inquiry. Please try again.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
